import React from 'react';

import styled from 'styled-components';

import { DiskBadge } from 'components/common/DiskBadge/DiskBadge';
import { Icon } from 'components/common/Icon/Icon';
import { Text } from 'components/common/Text/Text';
import { LightText } from 'components/no-auth/common/fromAuth/LightText/LightText';
import { PromoText } from 'components/no-auth/common/promo-text/PromoText';

import {
    DELIVERY_BANK_ACCOUNT,
    DELIVERY_BANK_CARD,
    DELIVERY_SEPA,
    DELIVERY_SWIFT,
    DELIVERY_UNIONPAY_BANK_CARD,
    DELIVERY_UNITELLER_MASTERCARD_BANK_CARD,
    DELIVERY_VISA_BANK_CARD,
    HARD_BLOCK,
    SOFT_BLOCK,
} from 'constants/Constants';
import { EMAIL, TEL, TEXT } from 'constants/FieldType';
import { DEFAULT_CURRENCY_ICON, URL_IMAGES_BANKS, URL_IMAGES_CURRENCIES } from 'constants/UrlConstants';
import { CANADA, GUATEMALA, ISRAEL, USA } from 'constants/WellKnownCountries';

import * as Sentry from '@sentry/react';
import promoColors from 'theme/promoColors';

export const resolveArguments = (message = '', args) => {
    const regex = /\{\d}/g;
    message = message === undefined || message === null ? '' : message;
    if (Array.isArray(args)) {
        let resolveMessage = message;
        const matchArray = message.match(regex);
        if (matchArray) {
            args.forEach((argument, index) => {
                resolveMessage = resolveMessage.replaceAll(`\{${index}\}`, argument);
            });
            if (matchArray.length !== args.length && !__isDev__) {
                resolveMessage = resolveMessage.replace(regex, '');
            }
        }
        return resolveMessage;
    } else if (isObject(args)) {
        let newMessage;
        const matchArray = message.match(regex);
        if (matchArray && matchArray[0]) {
            const newMessForFirst = message.replaceAll(`\{0\}`, args);
            if (matchArray.length > 1 && !__isDev__) {
                newMessage = newMessForFirst.replace(regex, '');
            } else {
                return newMessForFirst;
            }
            return newMessage;
        } else {
            return message;
        }
    } else {
        return message;
    }
};

export const getCurrentLang = (languages, locale) => {
    const lang = languages.find((el) => el.localeCode === locale);
    return lang ? lang.name : '';
};

// convert date from DD.MM.YYYY & DD/MM/YYYY to YYYY-MM-DD
export const getIsoDate = (dateStr) => {
    const dateArr = dateStr.split(dateStr[2]);
    return `${dateArr[2]}-${dateArr[1]}-${dateArr[0]}`;
};

// convert date from MM.DD.YYYY & MM/DD/YYYY to YYYY-MM-DD
export const getIsoDateUSA = (dateStr) => {
    const dateArr = dateStr.split(dateStr[2]);
    return `${dateArr[2]}-${dateArr[0]}-${dateArr[1]}`;
};

export const getYYYYMMDDDate = (date) => {
    const jsDate = new Date(date);
    return `${jsDate.getFullYear()}-${addZero(jsDate.getMonth() + 1)}-${addZero(jsDate.getDate())}`;
};
export const getJsDate2 = (jsonDate = {}) => {
    const strDate = `${jsonDate.year}-${addZero(jsonDate.monthValue)}-${addZero(jsonDate.dayOfMonth)}T${addZero(jsonDate.hour)}:${addZero(
        jsonDate.minute,
    )}:${addZero(jsonDate.second)}.${jsonDate.nano}Z`;
    return new Date(strDate);
};

export const getDDMMMYYYYDate = (date, lang) => {
    // convert date from "2023-08-02T19:07:01" to "02 Aug 2023"
    const jsDate = new Date(date);
    return `${addZero(jsDate.getDate())} ${jsDate.toLocaleString(lang, { month: 'short' })} ${addZero(jsDate.getFullYear())}`;
};

export const getDDMMYYYYNumericDate = (date, country) => {
    // format ex: '23.10.2024'
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return country === USA ? `${month}/${day}/${year}` : `${day}.${month}.${year}`;
};

export const getDayMonthDate = (date, lang, length) => {
    // convert date from "02.09.2023 19:07:01" to "09 Sep" or "09 September"
    const jsDate = new Date(date?.replace(/(\d{2}).(\d{2}).(\d{4})/, '$2/$1/$3'));
    return `${addZero(jsDate.getDate())} ${jsDate.toLocaleString(lang, { month: length })}`;
};

export const isDateToday = (date) => {
    const dateToCheck = new Date(date);
    const today = new Date();
    const isToday =
        dateToCheck.getDate() === today.getDate() && dateToCheck.getMonth() === today.getMonth() && dateToCheck.getFullYear() === today.getFullYear();
    return isToday;
};

export const addZero = (number) => (number < 10 ? '0' + number : number);

export const getDeltaSecondsBetweenDates = (dateStart, dateEnd) => {
    const mStart = dateStart.getTime();
    const mEnd = dateEnd.getTime();
    return ((mEnd - mStart) / 60) * 1000;
};

export const getGaActivityDate = (dateTime, lang) => {
    const jsDate = new Date(dateTime + 'Z');
    // format 14 Oct · Wed 09:09
    return `${addZero(jsDate.getDate())} ${jsDate.toLocaleString(lang, { month: 'short' }).replace('.', '')} · ${jsDate.toLocaleString(lang, {
        weekday: 'short',
    })} ${addZero(jsDate.getHours())}:${addZero(jsDate.getMinutes())}`;
};

export const getLimitEndDate = (date, lang) => {
    // format Tuesday, June 20, 2023 12:32 AM
    return new Intl.DateTimeFormat(`${lang}${window?.navigator?.language.substring(2)}`, { dateStyle: 'full', timeStyle: 'short' }).format(new Date(date));
};

export const getHistoryTransactionDate = (date, lang) => {
    const options = {
        month: 'short',
        day: 'numeric',
        weekday: 'short',
        hour: 'numeric',
        minute: '2-digit',
    };
    return new Intl.DateTimeFormat(`${lang}${window?.navigator?.language.substring(2)}`, options).format(new Date(date));
};

export const getHistoryTransactionFullDate = (date, lang, userCountry) => {
    const isUSAFormat = userCountry === USA && lang === 'en';
    const locale = isUSAFormat ? 'en-US' : `${lang}${window?.navigator?.language.substring(2) || ''}`;

    const dateOptions = {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
    };

    const timeOptions = {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
    };

    const formattedDate = new Intl.DateTimeFormat(locale, dateOptions).format(new Date(date));
    const formattedTime = new Intl.DateTimeFormat(locale, timeOptions).format(new Date(date));

    return {
        formattedDate,
        formattedTime,
    };
};

export const getDDMonEEEHHmmDate = (dateTime, lang) => {
    if (dateTime) {
        const jsDate = new Date(dateTime);
        // format 4 Oct · Wed 09:09
        return `${jsDate.getDate()} ${jsDate.toLocaleString(lang, { month: 'short' }).replace('.', '')} · ${jsDate.toLocaleString(lang, {
            weekday: 'short',
        })} ${addZero(jsDate.getHours())}:${addZero(jsDate.getMinutes())}`;
    }
};

export const getGMTdate = (dateTime) => {
    if (dateTime === undefined || dateTime === null) return;
    const jsDate = typeof dateTime === 'object' ? getJsDate2(dateTime) : new Date(dateTime);
    const h = Math.floor(Math.abs(jsDate.getTimezoneOffset()) / 60);
    const gmt = (jsDate.getTimezoneOffset() < 0 ? '+' : '-') + addZero(h) + ':' + addZero(Math.abs(jsDate.getTimezoneOffset()) - h * 60);
    return `${addZero(jsDate.getDate())}.${addZero(jsDate.getMonth() + 1)}.${jsDate.getFullYear()}, ${addZero(jsDate.getHours())}:${addZero(
        jsDate.getMinutes(),
    )} (GMT${gmt})`;
};

export const timer = (timerID, callFun, delay = 300) => {
    if (timerID) {
        clearTimeout(timerID);
    }
    timerID = setTimeout(() => {
        callFun();
    }, delay);
    return timerID;
};

export const isObject = (obj) => {
    return obj !== null && typeof obj !== 'undefined';
};
export const validOnMod10 = (cardNumber) => {
    var arr = cardNumber.split('').map(function (char, index) {
        var digit = parseInt(char);
        if ((index + cardNumber.length) % 2 === 0) {
            var digitX2 = digit * 2;
            return digitX2 > 9 ? digitX2 - 9 : digitX2;
        }
        return digit;
    });
    return !(
        arr.reduce(function (a, b) {
            return a + b;
        }, 0) % 10
    );
};
export const random128Hex = () => {
    const random16Hex = () => {
        return (0x10000 | (Math.random() * 0x10000)).toString(16).substr(1);
    };
    return (
        random16Hex() + random16Hex() + '-' + random16Hex() + '-' + random16Hex() + '-' + random16Hex() + '-' + random16Hex() + random16Hex() + random16Hex()
    );
};

export const getLangCountryPrefix = (lang, country) => {
    let prefix = '/';
    if (lang && typeof country === 'string') {
        prefix = `/${lang}-${country.toLowerCase()}`;
    } else if (lang) {
        prefix = `/${lang}`;
    }
    return prefix;
};
export const placeSrcSetAttr = (img1, img2) => {
    if (img1 && img2) return `${img1} 1x, ${img2} 2x`;
    if (img1 && !img2) return `${img1} 2x`;
};

export const isSpecialCountry = (countryId) => {
    return countryId === USA || countryId === CANADA;
};

export const getExtra = (extra, extraName) => {
    if (extra && typeof extra === 'string') {
        const extras = extra.split(';');
        const filtered = extras.filter((e) => e && e.split('=')[0] === extraName);
        return filtered && filtered[0] ? filtered[0].split('=')[1] : null;
    } else {
        return null;
    }
};

export const toLowerCase = (str) => {
    return str && typeof str === 'string' ? str.toLowerCase() : '';
};

export const toUpperCase = (str) => {
    return str && typeof str === 'string' ? str.toUpperCase() : '';
};

export const getEmptyStringIfNull = (str) => {
    return str === null ? '' : str;
};
export const isNotEmptyArray = (arr) => Array.isArray(arr) && arr.length > 0;

export const canShareDetails = () => {
    return navigator && navigator.share;
};

export const shareDetails = (title, text) => {
    if (canShareDetails()) {
        navigator
            .share({
                title: title,
                text: text,
            })
            .then(() => {
                console.log('Successfully shared');
            })
            .catch((error) => {
                console.error('Something went wrong sharing the block', error);
            });
    }
};
export const copyTextToClipboard = async (text) => {
    const textArea = document.createElement('textarea');
    textArea.style.background = 'transparent';
    textArea.style.position = 'fixed';
    textArea.style.top = 0;
    textArea.style.left = 0;
    textArea.value = text;

    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
        await navigator.clipboard.writeText(text);
    } catch (err) {
        Sentry.captureException(err, {
            level: 'info',
        });
        console.log('Oops, unable to copy');
        return false;
    } finally {
        document.body.removeChild(textArea);
    }

    return true;
};

export const getBankAlias = (alias, iconName = 'card', isDisabled) => {
    const logoSymbol = <Icon name={iconName} size="24px" />;
    return (
        <DiskBadge
            variant={alias ? 'hasBorderedImg' : 'tertiary'}
            src={alias ? `${URL_IMAGES_BANKS}/${alias}.png` : null}
            iconName={!alias ? iconName : null}
            isDisabled={isDisabled}
            logoSymbol={logoSymbol}
        />
    );
};
export const deletingWhiteSpaceInString = (str) => {
    if (typeof str === 'string') {
        const array = str.match(/(\d+)\s([\d\.]+)/) || [];
        if (array.length === 0) {
            return str;
        } else {
            return array[1] + array[2];
        }
    } else {
        return str;
    }
};
export const deleteArtifactsFromStr = (string) => {
    const regex = /\s*{.*}/;
    return string.replace(regex, '');
};

export const clabeCalcChecksum = (clabeNum) => {
    if (typeof clabeNum === 'undefined' || clabeNum.length !== 18) {
        return false;
    }
    let sum = 0;

    function add(digit, index) {
        sum += (parseInt(digit) * [3, 7, 1][index % 3]) % 10;
    }

    clabeNum.split('').slice(0, 17).forEach(add);
    return parseInt(clabeNum.substring(17, 18)) === (10 - (sum % 10)) % 10;
};

export const isCardExpired = (card) => {
    let month, year;
    const now = new Date();
    const currentYear = parseInt(now.getFullYear().toString().substr(-2));
    const currentMonth = now.getMonth() + 1;
    if (card && card.cardExpired) {
        month = parseInt(card.cardExpired.split('/')[0]);
        year = parseInt(card.cardExpired.split('/')[1]);
    } else if (card?.expMonth && card?.expYear) {
        month = card.expMonth;
        year = card.expYear;
    }
    return year < currentYear || (currentYear === year && month < currentMonth);
};

export const getFormattedCardOrAccountNumber = (number, deliveryMethod) => {
    if (number != null && (deliveryMethod === DELIVERY_BANK_ACCOUNT || deliveryMethod === DELIVERY_SWIFT || deliveryMethod === DELIVERY_SEPA)) {
        let formattedNumberArr = [];
        for (let i = 0; i < number.length; i += 4) {
            formattedNumberArr.push(number.substring(i, i + 4));
        }
        return formattedNumberArr.join(' ');
    } else {
        return number;
    }
};

export const getSortCodeWithHyphens = (sortCode) => {
    if (typeof sortCode === 'string' && sortCode.length > 0) {
        let formattedSortCodeArr = [];
        for (let i = 0; i < sortCode.length; i += 2) {
            formattedSortCodeArr.push(sortCode.substring(i, i + 2));
        }
        return formattedSortCodeArr.join('-');
    } else {
        return null;
    }
};

export const getReferenceCodeWithHyphen = (referenceCode) => {
    if (typeof referenceCode === 'string' && referenceCode.length > 0) {
        return referenceCode.replace(/^(\d{3})(\d{8})$/, '$1-$2');
    } else {
        return null;
    }
};

export const getFormattedPhoneNumber = (number, country) => {
    let formattedNumber;
    if (country === GUATEMALA) {
        const format = number.match(/^(\d{3})(\d{4})(\d{4})$/);
        if (format) {
            formattedNumber = `${format[1]} ${format[2]} ${format[3]}`;
            return formattedNumber;
        } else {
            return number;
        }
    } else {
        return number;
    }
};

export const getActualMaxAmountFrom = (userCountry, leftLimitAmount, canMakeDraftTransaction, restrictionPerTransaction) => {
    if (userCountry?.id === ISRAEL) {
        return restrictionPerTransaction.maxAmount;
    }

    return leftLimitAmount && !canMakeDraftTransaction && leftLimitAmount < restrictionPerTransaction.maxAmount
        ? leftLimitAmount
        : restrictionPerTransaction.maxAmount;
};

export const findErrorCodeAndMessage = (messageBodyError, serverErrors, cheque, paymentStatus, serverErrorCode) => {
    let errorCode = null;
    let errorMessage = null;
    if (messageBodyError?.length > 0) {
        messageBodyError.forEach((error) => {
            errorCode = error.errorCode;
            errorMessage = error.value;
        });
    } else if (Object.keys(serverErrors || {}).length > 0) {
        Object.values(serverErrors).forEach((errorField) => {
            errorCode = errorField.code;
            errorMessage = errorField.body;
        });
    } else if (cheque?.errorCode) {
        errorCode = cheque.errorCode;
        errorMessage = cheque.errorMessage;
    } else if (paymentStatus?.errorCode) {
        errorCode = paymentStatus.errorCode;
        errorMessage = paymentStatus.message;
    } else if (serverErrorCode) {
        errorCode = serverErrorCode.code;
        errorMessage = serverErrorCode.body;
    }
    return { errorCode, errorMessage };
};

const softBlockErrors = [
    160, 611, 612, 613, 615, 616, 617, 618, 619, 620, 621, 622, 631, 632, 634, 635, 636, 637, 638, 639, 640, 641, 642, 644, 649, 656, 6001, 6002, 6003, 6005,
    6006, 6007, 6008, 6010, 6011, 6012, 6013, 6014, 6015, 6016, 6017, 6018, 6019, 6020, 6021, 6022, 6023, 6024, 6025, 6026, 6027, 6028, 6029, 6030, 6031, 6032,
    6033, 6034, 6035, 6036, 6037, 6038, 6039, 6040, 6041, 6042, 6043, 6044, 6045, 6046, 6047, 6048, 6049, 6050, 6051, 6052, 6054, 6055, 6056, 6057, 6063, 6064,
    6065,
];
const hardBlockErrors = [150, 153, 655, 6059, 6060, 6061, 6062];
export const showErrorType = (messageBodyError, serverErrors, chequeError, paymentStatusError, serverErrorCode) => {
    const { errorCode } = findErrorCodeAndMessage(messageBodyError, serverErrors, chequeError, paymentStatusError, serverErrorCode);
    let errorType;
    if (softBlockErrors.includes(errorCode)) {
        errorType = SOFT_BLOCK;
    } else if (hardBlockErrors.includes(errorCode)) {
        errorType = HARD_BLOCK;
    }
    return errorType;
};

export const isSoftOrHardServerError = (error) => {
    return softBlockErrors.includes(error) || hardBlockErrors.includes(error);
};

export const getCurrencyIcon = (name) => {
    return `${URL_IMAGES_CURRENCIES}/currency-${name}.svg`;
};
export const onIconRenderError = (e) => {
    e.target.onerror = null;
    e.target.src = DEFAULT_CURRENCY_ICON;
};
export const capitalizeFirstLetters = (str) => {
    if (str && typeof str === 'string') {
        const arr = str.toLowerCase().split(' ');
        return arr.map((item) => item[0].toUpperCase() + item.substring(1)).join(' ');
    }
    return '';
};
export const getReference = (referenceNumber, paymentId) => {
    let reference;
    if (!!referenceNumber) {
        reference = referenceNumber;
    } else {
        reference = paymentId;
    }
    return getReferenceCodeWithHyphen(reference);
};

export const UUID = () => {
    const lut = [];
    for (let i = 0; i < 256; i++) {
        lut[i] = (i < 16 ? '0' : '') + i.toString(16);
    }

    const d0 = (Math.random() * 0xffffffff) | 0;
    const d1 = (Math.random() * 0xffffffff) | 0;
    const d2 = (Math.random() * 0xffffffff) | 0;
    const d3 = (Math.random() * 0xffffffff) | 0;
    return (
        lut[d0 & 0xff] +
        lut[(d0 >> 8) & 0xff] +
        lut[(d0 >> 16) & 0xff] +
        lut[(d0 >> 24) & 0xff] +
        '-' +
        lut[d1 & 0xff] +
        lut[(d1 >> 8) & 0xff] +
        '-' +
        lut[((d1 >> 16) & 0x0f) | 0x40] +
        lut[(d1 >> 24) & 0xff] +
        '-' +
        lut[(d2 & 0x3f) | 0x80] +
        lut[(d2 >> 8) & 0xff] +
        '-' +
        lut[(d2 >> 16) & 0xff] +
        lut[(d2 >> 24) & 0xff] +
        lut[d3 & 0xff] +
        lut[(d3 >> 8) & 0xff] +
        lut[(d3 >> 16) & 0xff] +
        lut[(d3 >> 24) & 0xff]
    );
};

export const getRepeatQueryParams = () => {
    let repeatId = null,
        recipientId = null;
    if (__isBrowser__) {
        const url = new URL(window.location);
        repeatId = url.searchParams.get('repeatId');
        recipientId = url.searchParams.get('recipientId');
    }
    return { repeatId, recipientId };
};

export const getCurrentSystemTheme = () => {
    let theme = 'light';
    if (typeof window !== 'undefined' && window?.matchMedia) {
        const isMatchesDark = window.matchMedia('(prefers-color-scheme: dark)')?.matches;
        theme = isMatchesDark ? 'dark' : 'light';
    }
    return theme;
};

export const focusFirstInput = () => {
    const inputs = document.querySelectorAll('input');
    const types = [TEXT, TEL, EMAIL];

    if (inputs.length > 0) {
        const firstInput = inputs[0];
        const isValidInput = types.includes(firstInput.type) && !firstInput.readOnly && firstInput.offsetParent !== null;
        if (isValidInput) {
            firstInput.focus();
        }
    }
};

const escapeRegExp = (string) => {
    return string.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');
};

const Highlight = styled.span`
    color: ${(props) => (props.useThemeColor ? props.theme.purple_100 : props.isPromo ? promoColors.primary : props.theme.purple_100)};
`;

export const getHighlightedText = (query, text, componentType = 'text', style = {}, useThemeColor = false) => {
    const escapedQuery = escapeRegExp(query);
    const parts = text?.split(new RegExp(`(${escapedQuery})`, 'gi'));
    const TextComponent = componentType === 'lightText' ? LightText : componentType === 'promoText' ? PromoText : Text;
    const isPromo = componentType === 'lightText' || componentType === 'promoText';

    return (
        <TextComponent
            color={style.basicColor || 'darkGray'}
            variant={style.variant || null}
            ellipsis={style.ellipsis || null}
            smallCaps={style.smallCaps || null}
            useThemeColor={useThemeColor}
        >
            {parts.map((part, i) =>
                part.toLowerCase() === query?.toLowerCase() ? (
                    <Highlight key={`${text}-${i}`} isPromo={isPromo} useThemeColor={useThemeColor}>
                        {part}
                    </Highlight>
                ) : (
                    <React.Fragment key={`${text}-${i}`}>{part}</React.Fragment>
                ),
            )}
        </TextComponent>
    );
};
